import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';

function PremiumCodes() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState(null);
  const [redemptionModal, setRedemptionModal] = useState({
    open: false,
    code: null,
    items: [],
    page: 1,
    pages: 0,
    total: 0,
    loading: false,
  });
  const [form, setForm] = useState({
    quantity: 10,
    duration_days: 7,
    is_unlimited: false,
    max_redemptions: 1,
    activation_expires_at: '',
    note: '',
    prefix: 'LWM',
  });

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/premium-codes');
      setCodes(response.data || []);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to fetch premium codes.' });
    } finally {
      setLoading(false);
    }
  };

  const generatedCodesText = useMemo(() => codes.slice(0, 50).map((item) => item.code).join('\n'), [codes]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setCreating(true);
    setMessage(null);

    try {
      const payload = {
        ...form,
        quantity: Number(form.quantity),
        duration_days: form.is_unlimited ? null : Number(form.duration_days),
        max_redemptions: Number(form.max_redemptions),
        activation_expires_at: form.activation_expires_at || null,
      };
      const response = await api.post('/admin/premium-codes', payload);
      setCodes((prev) => [...(response.data || []), ...prev]);
      setMessage({ type: 'success', text: `${response.data?.length || 0} premium code created.` });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to create premium codes.' });
    } finally {
      setCreating(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const toggleStatus = async (code) => {
    try {
      const nextStatus = code.status === 'active' ? 'disabled' : 'active';
      const response = await api.put(`/admin/premium-codes/${code._id}`, { status: nextStatus });
      setCodes((prev) => prev.map((item) => (
        item._id === code._id ? { ...item, ...response.data } : item
      )));
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update code status.' });
    }
  };

  const fetchRedemptions = async (code, page = 1) => {
    setRedemptionModal((prev) => ({
      ...prev,
      open: true,
      code,
      page,
      loading: true,
    }));

    try {
      const response = await api.get(`/admin/premium-codes/${code._id}/redemptions?page=${page}&limit=100`);
      setRedemptionModal({
        open: true,
        code,
        items: response.data?.items || [],
        page: response.data?.page || page,
        pages: response.data?.pages || 0,
        total: response.data?.total || 0,
        loading: false,
      });
    } catch (error) {
      setRedemptionModal((prev) => ({ ...prev, loading: false }));
      setMessage({ type: 'error', text: 'Failed to fetch redemptions.' });
    }
  };

  const closeRedemptionModal = () => {
    setRedemptionModal({
      open: false,
      code: null,
      items: [],
      page: 1,
      pages: 0,
      total: 0,
      loading: false,
    });
  };

  const copyRecentCodes = async () => {
    try {
      await navigator.clipboard.writeText(generatedCodesText);
      setMessage({ type: 'success', text: 'Recent codes copied.' });
    } catch {
      setMessage({ type: 'error', text: 'Copy failed.' });
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Premium Codes</h1>
        <p className="page-subtitle">Generate redeemable premium access codes without changing the purchase flow.</p>
      </div>

      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          {message.text}
        </div>
      )}

      <form className="card" onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Quantity</label>
            <input
              className="form-input"
              type="number"
              min="1"
              max="1000"
              value={form.quantity}
              onChange={(event) => setForm({ ...form, quantity: event.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Prefix</label>
            <input
              className="form-input"
              value={form.prefix}
              maxLength={8}
              onChange={(event) => setForm({ ...form, prefix: event.target.value.toUpperCase() })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Max redemptions per code</label>
            <input
              className="form-input"
              type="number"
              min="1"
              value={form.max_redemptions}
              onChange={(event) => setForm({ ...form, max_redemptions: event.target.value })}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Duration days</label>
            <input
              className="form-input"
              type="number"
              min="1"
              disabled={form.is_unlimited}
              value={form.duration_days}
              onChange={(event) => setForm({ ...form, duration_days: event.target.value })}
            />
          </div>
          <label className="form-group premium-toggle">
            <input
              type="checkbox"
              checked={form.is_unlimited}
              onChange={(event) => setForm({ ...form, is_unlimited: event.target.checked })}
            />
            <span>Unlimited premium</span>
          </label>
          <div className="form-group">
            <label className="form-label">Activation expires at</label>
            <input
              className="form-input"
              type="date"
              value={form.activation_expires_at}
              onChange={(event) => setForm({ ...form, activation_expires_at: event.target.value })}
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Note</label>
          <input
            className="form-input"
            placeholder="Campaign, partner, school, influencer..."
            value={form.note}
            onChange={(event) => setForm({ ...form, note: event.target.value })}
          />
        </div>

        <button className="btn btn-primary" type="submit" disabled={creating}>
          {creating ? 'Creating...' : 'Create Codes'}
        </button>
      </form>

      <div className="table-container">
        <div className="table-toolbar">
          <h2>Generated Codes</h2>
          <button className="btn btn-secondary" onClick={copyRecentCodes} disabled={!codes.length}>
            Copy recent 50
          </button>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Duration</th>
              <th>Usage</th>
              <th>Status</th>
              <th>Activation Expiry</th>
              <th>Note</th>
              <th>Created</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {codes.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>
                  No premium codes yet
                </td>
              </tr>
            ) : (
              codes.map((code) => (
                <tr key={code._id}>
                  <td><strong>{code.code}</strong></td>
                  <td>{code.is_unlimited ? 'Unlimited' : `${code.duration_days} days`}</td>
                  <td>
                    <button
                      className="link-button"
                      type="button"
                      disabled={!code.redeemed_count}
                      onClick={() => fetchRedemptions(code, 1)}
                    >
                      {code.redeemed_count || 0} / {code.max_redemptions}
                    </button>
                  </td>
                  <td>
                    <span className={`badge ${code.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
                      {code.status}
                    </span>
                  </td>
                  <td>{formatDate(code.activation_expires_at)}</td>
                  <td>{code.note || '-'}</td>
                  <td>{formatDate(code.createdAt)}</td>
                  <td>
                    <button className="btn-icon" onClick={() => toggleStatus(code)}>
                      {code.status === 'active' ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {redemptionModal.open && (
        <div className="modal-backdrop" onClick={closeRedemptionModal}>
          <div className="admin-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Code Redemptions</h2>
                <p>{redemptionModal.code?.code} · {redemptionModal.total} users</p>
              </div>
              <button className="btn-icon" onClick={closeRedemptionModal}>Close</button>
            </div>

            {redemptionModal.loading ? (
              <div className="loading-container" style={{ minHeight: 180 }}>
                <div className="loading"></div>
              </div>
            ) : (
              <>
                <table className="table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Redeemed At</th>
                      <th>Premium Ends</th>
                    </tr>
                  </thead>
                  <tbody>
                    {redemptionModal.items.length === 0 ? (
                      <tr>
                        <td colSpan="4" style={{ textAlign: 'center', padding: '32px' }}>
                          No redemptions yet
                        </td>
                      </tr>
                    ) : (
                      redemptionModal.items.map((item) => (
                        <tr key={item._id}>
                          <td><strong>{item.user?.username || 'Unknown user'}</strong></td>
                          <td>{item.user?.email || '-'}</td>
                          <td>{formatDate(item.redeemed_at)}</td>
                          <td>{item.is_unlimited ? 'Unlimited' : formatDate(item.premium_ends_at)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                <div className="pagination-row">
                  <button
                    className="btn btn-secondary"
                    disabled={redemptionModal.page <= 1}
                    onClick={() => fetchRedemptions(redemptionModal.code, redemptionModal.page - 1)}
                  >
                    Previous
                  </button>
                  <span>
                    Page {redemptionModal.page} / {Math.max(redemptionModal.pages, 1)}
                  </span>
                  <button
                    className="btn btn-secondary"
                    disabled={redemptionModal.page >= redemptionModal.pages}
                    onClick={() => fetchRedemptions(redemptionModal.code, redemptionModal.page + 1)}
                  >
                    Next
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PremiumCodes;
